import React from 'react'

import {AnalyticsDataContextProvider} from '../analytics/AnalyticsDataContextProvider'
import {OrderProcessContext} from '../context/OrderProcessContext'
import {
  SlotManagementContext,
  SlotManagementContextProps
} from '../slotManagement/context/SlotManagementContext'
import {useSlotManagementState} from '../slotManagement/context/useSlotManagementState'
import {SlotManagementConfig} from '../slotManagement/types'

import {OrderIntakeAnalyticsData} from './buildOrderIntakeAnalyticsData'
import {OrderIntakeProcessContextType} from './context/OrderIntakeProcessContext.types'
import {useOrderIntakeAvailableTimeSlots} from './slotManagement/hooks/useOrderIntakeAvailableTimeSlots'
import {useOrderIntakeSlotValidation} from './slotManagement/hooks/useOrderIntakeSlotValidation'
import {useOrderIntakeSlotValidationMutation} from './slotManagement/hooks/useOrderIntakeSlotValidationMutation'

const SLOT_MANAGEMENT_HOOKS = {
  useAvailableTimeSlots: useOrderIntakeAvailableTimeSlots,
  useSlotValidation: useOrderIntakeSlotValidation,
  useSlotValidationMutation: useOrderIntakeSlotValidationMutation
}

type OrderIntakeProcessShellProps = React.PropsWithChildren & {
  analyticsData: OrderIntakeAnalyticsData
  orderIntakeProcessData: OrderIntakeProcessContextType
  slotManagementConfig: SlotManagementConfig
  slotManagementHooks?: SlotManagementContextProps['hooks']
  renderConfigurationProviders: (children: React.ReactNode) => React.ReactNode
  renderFormProvider: (children: React.ReactNode) => React.ReactNode
}

export const OrderIntakeProcessShell = ({
  analyticsData,
  children,
  orderIntakeProcessData,
  renderConfigurationProviders,
  renderFormProvider,
  slotManagementConfig,
  slotManagementHooks = SLOT_MANAGEMENT_HOOKS
}: OrderIntakeProcessShellProps) => {
  return (
    <OrderProcessContext.Provider value={orderIntakeProcessData}>
      {renderConfigurationProviders(
        <AnalyticsDataContextProvider<OrderIntakeAnalyticsData> analyticsData={analyticsData}>
          {renderFormProvider(
            <SlotManagementProvider config={slotManagementConfig} hooks={slotManagementHooks}>
              {children}
            </SlotManagementProvider>
          )}
        </AnalyticsDataContextProvider>
      )}
    </OrderProcessContext.Provider>
  )
}

const SlotManagementProvider = ({
  children,
  config,
  hooks
}: React.PropsWithChildren<{
  config: SlotManagementConfig
  hooks: SlotManagementContextProps['hooks']
}>) => {
  const slotManagementState = useSlotManagementState(config, hooks)

  return <SlotManagementContext.Provider value={slotManagementState}>{children}</SlotManagementContext.Provider>
}